/**
 * Base Application Error Class
 *
 * Why: Foundation for all application errors
 * What: Base class with common error properties and methods
 *
 * Benefits:
 *      - Consistent error structure across the application
 *      - Rich metadata for debugging and monitoring
 *      - Serializable for logging and reporting
 *      - Distinguishes operational errors from programmer errors
 *
 * Design:
 *      - Extends native Error for stack trace support
 *      - Uses readonly properties to prevent mutation
 *      - Includes isOperational flag to distinguish expected errors
 *      - Supports serialization for external services
 */

import {
  ErrorCategory,
  type ErrorContext,
  ErrorSeverity,
  type SerializedError,
  type ValidationFieldError,
} from "../../types";
import { ERROR_CODES, type ErrorCode } from "../../constants";

/**
 * Options for creating an AppError
 */
export interface AppErrorOptions {
  /** HTTP status code (0 for non HTTP erros) */
  statusCode?: number;
  /** Application error code */
  code?: ErrorCode;
  /** Error category for routing/handling */
  category?: ErrorCategory;
  /** Error severity for logging/alerting */
  severity?: ErrorSeverity;
  /** Whether this is an expected error (not a bug) */
  isOperational?: boolean;
  /** Whether the operation can be retried */
  isRetryable?: boolean;
  /** Additional context about the error */
  context?: ErrorContext;
  /** Validation fields Errors (for form Errors) */
  validationErrors?: ValidationFieldError[];
  /** The original error that caused this error */
  cause?: unknown;
}

/**
 * Base Application Error
 *
 * All custom errors in the application  should extends  this class
 * This ensures consistent error handling and logging
 */
export class AppError extends Error {
  /**
   * Http status code
   * 0 for non-HTTP errors (e.g network errors)
   */
  public readonly statusCode: number;
  /**
   * Application Error Code
   * Unique identifier for this error type
   */
  public readonly code: ErrorCode;
  /**
   * Error categoty
   * Used for routing to appropriate handler
   */
  public readonly category: ErrorCategory;
  /**
   * Error severity
   * Used for logging level and alerting
   */
  public readonly severity: ErrorSeverity;

  /**
   * Whether this error is operational (expected) vs programmer error
   *
   * Operational errors: Invalid Input,network failure
   * Programmer errors: Null referance,type errors
   * Operational errors can be handled gracefully
   * Programmer errors should crash and restart ( in sever cases)
   */
  public readonly isOperational: boolean;

  /**
   * Whether the failed operation can be retried
   *
   * Used to show retry buttons in UI and enable automatic retry in React Query
   */
  public readonly isRetryable: boolean;
  /**
   * When error occured
   */
  public readonly timestamp: Date;

  /**
   * Additional context about the error
   * Helps with debugging and monitoring
   */
  public readonly context?: ErrorContext;
  /**
   * Validation failed errors
   * Used for displaying form field errors
   */
  public readonly validationErrors?: ValidationFieldError[];
  /**
   * The original error that caused this error
   * Preserved for debugging and logging
   */
  public readonly cause?: unknown;

  constructor(message: string, options: AppErrorOptions = {}) {
    // call parent constructor with message
    super(message);

    // Maintain proper prototype chain
    // Required for instanceof to work correctly
    Object.setPrototypeOf(this, new.target.prototype);

    // Set error name to class name
    this.name = this.constructor.name;

    // Set properties with defaults
    this.statusCode = options.statusCode ?? 500;
    this.code = options.code ?? ERROR_CODES.UNKNOWN_ERROR;
    this.category = options.category ?? ErrorCategory.UNKNOWN;
    this.severity = options.severity ?? ErrorSeverity.MEDIUM;
    this.isOperational = options.isOperational ?? true;
    this.isRetryable = options.isRetryable ?? false;
    this.timestamp = new Date();
    this.context = options.context;
    this.validationErrors = options.validationErrors;
    this.cause = options.cause;

    // Capture stack trace,excluding constructor
    // Error.captureStackTrace(this, this.constructor);
  }
  /**
   * Get user-friendly error message
   *
   * Returns message for operational errors.
   * generic message for programmer errors (to hide technical details)
   */
  public getUserMessage(): string {
    if (this.isOperational) {
      return this.message;
    }
    return "An unexpected error occured. Please try again";
  }

  /**
   * Serialize error for logging and reporting
   *
   * Created a plain object that can be:
   *    - Json straightfied for logs
   *    - Sent to error monitoring services
   *    - Stored in database
   */
  public serialize(): SerializedError {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      code: this.code,
      category: this.category,
      severity: this.severity,
      isRetryable: this.isRetryable,
      isOperational: this.isOperational,
      context: this.context,
      validationErrors: this.validationErrors,
      stack: this.stack,
      timestamp: this.timestamp.toISOString(),
    };
  }
  /**
   * Create new error with additional context
   * Userful for adding context as error propogate up the stack
   */
  public withContext(additionalContext: Partial<ErrorContext>): AppError {
    const mergedContext: ErrorContext = {
      ...this.context,
      ...additionalContext,
      timestamp: this.context?.timestamp ?? new Date(),
    };

    return new AppError(this.message, {
      statusCode: this.statusCode,
      code: this.code,
      severity: this.severity,
      category: this.category,
      isOperational: this.isOperational,
      isRetryable: this.isRetryable,
      context: mergedContext,
      validationErrors: this.validationErrors,
      cause: this.cause,
    });
  }
  /**
   * Check if error belongs to a specific category
   */
  public isCategory(category: ErrorCategory): boolean {
    return this.category === category;
  }
  /**
   * Check if error is at or above a severity level
   */
  public isAtLeastSeverity(severity: ErrorSeverity): boolean {
    const levels: ErrorSeverity[] = [
      ErrorSeverity.LOW,
      ErrorSeverity.MEDIUM,
      ErrorSeverity.HIGH,
      ErrorSeverity.CRITICAL,
    ];
    return levels.indexOf(this.severity) >= levels.indexOf(severity);
  }

  /**
   * Check if this error wraps another error
   *
   */
  public hasCause(): boolean {
    return this.cause !== undefined;
  }
  /**
   * Get the root cause error
   * Traverses the cause chain to find the original error
   */
  public getRootCause(): unknown {
    let current: unknown = this;
    while (current instanceof AppError && current.cause) {
      current = current.cause;
    }
    return current;
  }
  /** Convert to plain object(for JSON.stringify) */
  public toJSON(): SerializedError {
    return this.serialize();
  }
  /**
   * String representation of error
   */
  public toString(): string {
    return `${this.name} [${this.code}]: ${this.message}`;
  }
}
